import type { CollectionVisibility } from '../types';

/**
 * Reads collection visibility from Firestore data, with migration from the old
 * `allowDirectoryListing` boolean so existing users don't lose their setting.
 */
export function resolveCollectionVisibility(
  data: Record<string, unknown> | null,
): CollectionVisibility {
  if (!data) return 'private';
  if (data.collectionVisibility === 'public') return 'public';
  if (data.collectionVisibility === 'team') return 'team';
  if (data.collectionVisibility === 'private') return 'private';
  if (data.allowDirectoryListing === true) return 'public';
  return 'private';
}

export function resolveTeamId(data: Record<string, unknown> | null): string | null {
  if (!data) return null;
  return typeof data.teamId === 'string' && data.teamId ? data.teamId : null;
}

/**
 * Apply live user-doc fields without allocating a new object when nothing
 * changed. Callers that key Firestore listeners on `user` must not see a new
 * reference on every snapshot.
 *
 * `collectionSummary` is deliberately absent: it changes on every card add, so
 * including it here reallocated the user object on every mutation and forced
 * those listeners to re-subscribe. It is published through
 * `useCollectionSummary()` instead.
 */
export function mergeUserDocLiveFields<
  T extends {
    teamId: string | null;
    collectionVisibility: CollectionVisibility;
    binderCount?: number | null;
  },
>(prev: T, data: Record<string, unknown>): T {
  const teamId = resolveTeamId(data);
  const collectionVisibility = resolveCollectionVisibility(data);
  const binderCount = typeof data.binderCount === 'number' ? data.binderCount : prev.binderCount ?? null;
  if (
    prev.teamId === teamId &&
    prev.collectionVisibility === collectionVisibility &&
    (prev.binderCount ?? null) === binderCount
  ) {
    return prev;
  }
  return { ...prev, teamId, collectionVisibility, binderCount };
}
